/**
 * ──────────────────────────────────────────────────────────────────────────
 *  CONTEXT-LOCKED PALETTE — CIMD 2021 quintile family (Task #234,
 *  design-council PART III Law 1/Law 4; operator-cop-doctrine §3A.7 colour-
 *  context discipline).
 *
 *  This module is the SINGLE source of the StatCan CIMD 2021 quintile palette
 *  (the data itself is stored on the community row, migration 0068; the
 *  vocabulary/semantics live in shared-types/cimd). Quintiles are StatCan's
 *  own bands: 1 = least deprived … 5 = most deprived. Consumed ONLY by the
 *  community family:
 *    - the CommunityBody side-panel CIMD card (headline chip + dimension rows)
 *    - the CommunitiesLayer marker chip tint (the default-OFF
 *      "Vulnerability shading" sub-toggle, re-keyed from #189's derived band)
 *
 *  Palette (ambient register, Law 4: every hue S ≤ 45%, 35% ≤ L ≤ 62% —
 *  vulnerability is CONTEXT, never an alarm; no motion, ever). The ramp
 *  deliberately echoes the retired #189 band palette so the COP's visual
 *  language is continuous:
 *    unknown  grey        #6b7280   ← NEUTRAL-GREY (Law 1 no-data fallback)
 *    Q1       blue-grey   #5d7689   (least deprived)
 *    Q2       sage        #7d8a62
 *    Q3       sand        #b09b4f
 *    Q4       clay        #a8714a
 *    Q5       plum        #a14573   (most deprived) ← deliberately OFF the
 *                                     red band (red is reserved, Law 1/Law 2)
 *
 *  Colour is never load-bearing alone: every render of a quintile colour is
 *  paired with the quintile text (Law 1).
 *
 *  ⛔ Do NOT import from sibling palettes (./communityStatus etc. — enforced by
 *     the no-restricted-imports lint rule in eslint.config.js). Importing the
 *     vocabulary + types from @tsr/shared-types is fine (not a palette).
 * ──────────────────────────────────────────────────────────────────────────
 */
import {
  cimdQuintileDescriptor,
  formatCimdQuintile,
  type CimdQuintile,
} from '@tsr/shared-types';

/** The quintile hexes — the single source of truth. */
export const CIMD_QUINTILE_COLORS: Record<CimdQuintile | 'unknown', string> = {
  unknown: '#6b7280', // NEUTRAL-GREY (Law 1)
  1: '#5d7689', // muted blue-grey — least deprived
  2: '#7d8a62', // muted sage
  3: '#b09b4f', // muted sand
  4: '#a8714a', // muted clay
  5: '#a14573', // muted plum — most deprived, never a saturated red
};

export interface CimdQuintileStyle {
  /** 1..5, or null when the quintile is unknown (honest, never fabricated). */
  quintile: CimdQuintile | null;
  /** CSS hex for the chip tint. */
  color: string;
  /** Short label, e.g. "Q5 of 5" or "Unknown" — colour never load-bearing alone. */
  label: string;
  /** StatCan's endpoint wording ("least/most deprived"), null for Q2–Q4/unknown. */
  descriptor: string | null;
}

/**
 * Resolve the render style for one CIMD quintile value. Anything outside
 * StatCan's published 1..5 resolves to the honest 'unknown' style.
 */
export function cimdQuintileStyle(q: number | null | undefined): CimdQuintileStyle {
  const valid = q !== null && q !== undefined && Number.isInteger(q) && q >= 1 && q <= 5;
  if (!valid) {
    return { quintile: null, color: CIMD_QUINTILE_COLORS.unknown, label: 'Unknown', descriptor: null };
  }
  const quintile = q as CimdQuintile;
  return {
    quintile,
    color: CIMD_QUINTILE_COLORS[quintile],
    label: formatCimdQuintile(quintile),
    descriptor: cimdQuintileDescriptor(quintile),
  };
}
